import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import creditCardImage from "@/assets/img/creditCard.jpg";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Save, Trash2, Plus } from "lucide-react";

import CaseSizeCalculator from "@/modules/autres/CaseSizeCalculator";

export interface CaseSize {
  id?: number; // 'id' est optionnel car il n'existe pas pour une nouvelle taille non encore enregistrée
  name: string;
  caseSizePx: number;
}


export function DimensionEcranForm({ portal }: { portal: string }) {
  const [loading, setLoading] = useState(true);
  const [caseSizes, setCaseSizes] = useState<CaseSize[]>([]);
  const [selectedCaseSize, setSelectedCaseSize] = useState<CaseSize | null>(null);
  

  useEffect(() => {
    axios
      .get(`/${portal}/caseSizes`)
      .then((response) => {
        setCaseSizes(response.data);
        if (response.data.length > 0) {
          setSelectedCaseSize(response.data[0]); // Sélectionnez le premier par défaut
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des tailles de cases:", error);
        toast.error("Erreur lors de la récupération des tailles de cases");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [portal]);

  const handleSave = () => {
    if (!selectedCaseSize) {
      return;
    }

    const { id, name, caseSizePx } = selectedCaseSize;

    if (id) {
      // Mettre à jour la taille de case existante
      axios
        .put(`/${portal}/caseSizes/${id}`, {
          name,
          caseSizePx: Math.round(caseSizePx),
        })
        .then((response) => {
          toast("Taille de la case mise à jour", {
            position: "top-center",
            autoClose: 2000,
          });
          // Mettre à jour la liste des tailles de case
          setCaseSizes((prevCaseSizes) =>
            prevCaseSizes.map((cs) => (cs.id === id ? response.data : cs))
          );
        })
        .catch((error) => {
          console.error("Erreur lors de la modification de la taille de la case:", error);
          toast.error("Erreur lors de la modification de la taille de la case", {
            position: "top-center",
            autoClose: 2000,
          });
        });
    } else {
      // Créer une nouvelle taille de case
      axios
        .post(`/${portal}/caseSizes`, {
          name,
          caseSizePx: Math.round(caseSizePx),
        })
        .then((response) => {
          toast("Nouvelle taille de case créée", {
            position: "top-center",
            autoClose: 2000,
          });
          setCaseSizes((prevCaseSizes) => [...prevCaseSizes, response.data]);
          setSelectedCaseSize(response.data);
        })
        .catch((error) => {
          console.error("Erreur lors de la création de la taille de la case:", error);
          toast.error("Erreur lors de la création de la taille de la case", {
            position: "top-center",
            autoClose: 2000,
          });
        });
    }
  };

  const handleDelete = () => {
    if (!selectedCaseSize || !selectedCaseSize.id) {
      return;
    }

    axios
      .delete(`/${portal}/caseSizes/${selectedCaseSize.id}`)
      .then(() => {
        toast("Taille de la case supprimée", {
          position: "top-center",
          autoClose: 2000,
        });
        setCaseSizes((prevCaseSizes) =>
          prevCaseSizes.filter((cs) => cs.id !== selectedCaseSize.id)
        );
        setSelectedCaseSize(null);
      })
      .catch((error) => {
        console.error("Erreur lors de la suppression de la taille de la case:", error);
        toast.error("Erreur lors de la suppression de la taille de la case", {
          position: "top-center",
          autoClose: 2000,
        });
      });
  };

  const handleCreateNew = () => {
    setSelectedCaseSize({
      name: "",
      caseSizePx: 50,
    });
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <Card className="w-5/6 min-h-5/6 mt-11 mb-11">
      <CardHeader className="border-b mb-6">
        <CardTitle>Taille de la case</CardTitle>
        <CardDescription>
          Ajustez la taille de la case pour qu'elle fasse 4 cm de côté. Si la carte bleue reste trop petite au maximum du curseur, utilisez le champ numérique pour dépasser la plage et ajuster plus finement.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center">
          <div className="w-full mb-4">
            <div className="flex justify-between items-center">
              <select
                value={selectedCaseSize?.id || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const cs = caseSizes.find((cs) => cs.id === Number(selectedId));
                  setSelectedCaseSize(cs || null);
                }}
                className="border p-2"
              >
                {caseSizes.map((cs) => (
                  <option key={cs.id} value={cs.id}>
                    {cs.name}
                  </option>
                ))}
                {selectedCaseSize && !selectedCaseSize.id && (
                  <option value="">Nouvelle taille de case</option>
                )}
              </select>
              <Button type="button" onClick={handleCreateNew}>
                <Plus className="mr-2" /> Nouvelle taille de case
              </Button>
            </div>
          </div>
          {selectedCaseSize && (
            <>
              <div className="mt-6 w-full">
                <input
                  type="text"
                  placeholder="Nom de la taille de case"
                  value={selectedCaseSize.name}
                  onChange={(e) =>
                    setSelectedCaseSize({
                      ...selectedCaseSize,
                      name: e.target.value,
                    })
                  }
                  className="border p-2 w-full mb-4"
                />
                <img
                  src={creditCardImage}
                  alt="Carte de crédit de référence"
                  className="mx-auto"
                  style={{
                    width: `${(8.56 * selectedCaseSize.caseSizePx) / 4}px`,
                    // éviter un débordement horizontal massif tout en laissant une grande amplitude
                    maxWidth: "95vw",
                  }}
                />
                <CaseSizeCalculator
                  setCaseSizePx={(value) =>
                    setSelectedCaseSize({
                      ...selectedCaseSize,
                      caseSizePx: Math.round(value),
                    })
                  }
                  caseSizePx={selectedCaseSize.caseSizePx}
                />
              </div>
              <CardFooter className="flex justify-center border-t">
                <Button type="button" className="mt-6 mr-4" onClick={handleSave}>
                  <Save className="mr-2" />{" "}
                  {selectedCaseSize.id ? "Enregistrer" : "Créer"}
                </Button>
                {selectedCaseSize.id && (
                  <Button
                    type="button"
                    variant="destructive"
                    className="mt-6"
                    onClick={handleDelete}
                  >
                    <Trash2 className="mr-2" /> Supprimer
                  </Button>
                )}
              </CardFooter>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
